import { type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import constate from "constate";
import { useState } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { z } from "zod";

import { type PlacementCandidate } from "../../PlacementCandidate/types";
import { type PlacementDetail } from "../api/useGetPlacement";
import { useNotifyRecruiterForScreening } from "./useNotifyRecruiterForScreening";
import {
  type RecruiterScreeningConfig,
  usePlacementRecruiterScreeningConfig,
} from "./usePlacementRecruiterScreeningConfig";

const recruiterBookingContactDetailsFormSchema = z.object({
  phone: z
    .string()
    .min(1)
    .refine((v) => v !== "(___) ___-____", {
      // This is what react-imask returns when there is no input
      message: "Please enter a valid phone number",
    }),
});

type RecruiterBookingFlowPath = "landing" | "call-me-now" | "schedule-for-later";
type RecruiterBookingFlowStage = "contact-details" | "confirmation";

interface UseRecruiterBookingFlowProps {
  placementCandidate: PlacementCandidate;
  source: "banner" | "detail-cta";
  placement?: PlacementDetail;
}

export interface RecruiterBookingFlowState {
  modalState: UseModalState;
  reset: () => void;
  onCallMeNow: (data: z.infer<typeof recruiterBookingContactDetailsFormSchema>) => Promise<void>;
  onScheduleForLater: () => Promise<void>;
  path: RecruiterBookingFlowPath;
  setPath: React.Dispatch<React.SetStateAction<RecruiterBookingFlowPath>>;
  stage: RecruiterBookingFlowStage;
  setStage: React.Dispatch<React.SetStateAction<RecruiterBookingFlowStage>>;
  screeningConfig: RecruiterScreeningConfig;
  contactDetailsForm: UseFormReturn<z.infer<typeof recruiterBookingContactDetailsFormSchema>>;
  isLoading: boolean;
}

function useRecruiterBookingFlow(props: UseRecruiterBookingFlowProps): RecruiterBookingFlowState {
  const modalState = useModalState();
  const screeningConfig = usePlacementRecruiterScreeningConfig();
  const worker = useDefinedWorker();
  const { placementCandidate, source, placement } = props;

  const [path, setPath] = useState<RecruiterBookingFlowPath>("landing");
  const [stage, setStage] = useState<RecruiterBookingFlowStage>("contact-details");

  const contactDetailsForm = useForm<z.infer<typeof recruiterBookingContactDetailsFormSchema>>({
    resolver: zodResolver(recruiterBookingContactDetailsFormSchema),
    defaultValues: {
      phone: worker.phone ?? "",
    },
  });

  const {
    mutateAsync: notifyRecruiterForScreening,
    isLoading: isNotifyingRecruiterForScreeningLoading,
  } = useNotifyRecruiterForScreening({
    onSuccess: () => {
      setStage("confirmation");
    },
  });

  function reset() {
    setPath("landing");
    setStage("contact-details");
    contactDetailsForm.reset();
  }

  function onConfirmed() {
    logEvent(APP_V2_APP_EVENTS.PLACEMENT_RECRUITER_SCREENING_CALL_CONFIRMED, {
      placementCandidateId: placementCandidate.id,
      path,
      stage,
      showCallMeNow: screeningConfig.showCallMeNow,
    });
  }

  async function onCallMeNow(data: z.infer<typeof recruiterBookingContactDetailsFormSchema>) {
    onConfirmed();

    // Send a slack notification
    await notifyRecruiterForScreening({
      source,
      contactPhone: data.phone,
      ...(isDefined(placement) && { placementId: placement.id }),
    });
  }

  async function onScheduleForLater() {
    onConfirmed();

    // Open the calendly link for scheduling
    await openInBrowser({
      url: screeningConfig.screeningCalendlyLink,
      presentationStyle: "fullscreen",
    });
  }

  return {
    modalState,
    reset,
    onCallMeNow,
    onScheduleForLater,
    path,
    setPath,
    stage,
    setStage,
    screeningConfig,
    contactDetailsForm,
    isLoading: isNotifyingRecruiterForScreeningLoading,
  };
}

export const [RecruiterBookingFlowProvider, useRecruiterBookingFlowContext] =
  constate(useRecruiterBookingFlow);
